/**
 * useClientSessionToken — bridge a signed-in Clerk client to a portal token.
 *
 * Runs ONLY when:
 *   • enabled is true (default), AND
 *   • there is no magic-link token in play (the layout decides that), AND
 *   • Clerk reports signed-in, AND
 *   • useWorkspaceRole() === 'client'.
 *
 * Otherwise returns `state: 'no_token'` silently — no fetch, no error.
 *
 * Flow:
 *   1. GET /api/v1/client/me/cases
 *   2. 0 cases  → state: 'no_case'
 *   3. 1 case   → POST /cases/{id}/portal-session → state: 'ready' (token + expires_at + report_id)
 *   4. 2+ cases → state: 'picker' (cases array for a picker UI)
 *
 * The token lives in React state only — never the URL, history, or storage.
 * A 404/410 from portal-session POST means no grant — no retry.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useWorkspaceRole } from './useWorkspaceRole';
import { api } from '../utils/api';

export interface ClientSessionCase {
  report_id: string;
  client_name: string;
}

export type ClientSessionState =
  | { state: 'no_token' }
  | { state: 'loading' }
  | { state: 'ready'; token: string; expires_at: string; clarification_token?: string; report_id?: string }
  | { state: 'picker'; cases: ClientSessionCase[] }
  | { state: 'no_case' };

export function useClientSessionToken(options?: { enabled?: boolean }): ClientSessionState {
  const { enabled = true } = options || {};
  const { isSignedIn } = useAuth();
  const role = useWorkspaceRole();
  const [state, setState] = useState<ClientSessionState>({ state: 'no_token' });

  const isClient = isSignedIn === true && role === 'client';

  useEffect(() => {
    if (!enabled || !isClient) {
      setState({ state: 'no_token' });
      return;
    }

    let cancelled = false;

    async function load() {
      setState({ state: 'loading' });

      let cases: ClientSessionCase[];
      try {
        cases = await api.get<ClientSessionCase[]>('/api/v1/client/me/cases', {
          showErrorToast: false,
        });
      } catch (e: any) {
        if (!cancelled) setState({ state: 'no_token' });
        return;
      }
      if (cancelled) return;

      if (cases.length === 0) {
        setState({ state: 'no_case' });
        return;
      }

      if (cases.length > 1) {
        setState({ state: 'picker', cases });
        return;
      }

      await mintToken(cases[0].report_id);
    }

    async function mintToken(reportId: string) {
      try {
        const res = await api.post<{
          token: string;
          expires_at: string;
          clarification_token?: string;
        }>(
          `/api/v1/client/cases/${reportId}/portal-session`,
          undefined,
          { showErrorToast: false }
        );
        if (cancelled) return;
        setState({
          state: 'ready',
          token: res.token,
          expires_at: res.expires_at,
          report_id: reportId,
          ...(res.clarification_token ? { clarification_token: res.clarification_token } : {}),
        });
      } catch (e: any) {
        if (cancelled) return;
        setState({ state: 'no_token' });
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [isClient, enabled]);

  return state;
}

export default useClientSessionToken;
